import { Button } from '@/components/ui';
import { BunkerPriceChart } from './BunkerPriceChart';
import type {
  GroupedBunkerPrices,
  HistoricalPeriod,
  BunkerPriceHistory,
} from '@/types/bunkerPrices.types';

interface BunkerPriceAccordionItemProps {
  group: GroupedBunkerPrices;
  isExpanded: boolean;
  selectedPeriod: HistoricalPeriod;
  historyData: BunkerPriceHistory[];
  historyLoading: boolean;
  onToggle: () => void;
  onPeriodChange: (period: HistoricalPeriod) => void;
  periods: Array<{ value: number; label: string }>;
}

export function BunkerPriceAccordionItem({
  group,
  isExpanded,
  selectedPeriod,
  historyData,
  historyLoading,
  onToggle,
  onPeriodChange,
  periods,
}: BunkerPriceAccordionItemProps) {
  const formatPrice = (price: number) => {
    return `$${price.toFixed(2)}`;
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' });
  };

  const getChangeColorClass = (change: number): string => {
    if (change > 0) return 'text-green-600 dark:text-green-400';
    if (change < 0) return 'text-red-600 dark:text-red-400';
    return 'text-gray-500 dark:text-gray-400';
  };

  return (
    <div className="overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm transition-all dark:border-gray-700 dark:bg-gray-800">
      {/* Accordion Header */}
      <button
        onClick={onToggle}
        className="w-full px-3 py-2.5 text-left transition-colors hover:bg-gray-50 dark:hover:bg-gray-700/50 sm:px-6 sm:py-4"
      >
        <div>
          <div className="flex items-start justify-between gap-3 sm:gap-4">
            <div className="min-w-0 flex-1">
              {/* Port Name */}
              <h2 className="mb-2 text-base font-bold text-gray-900 dark:text-white sm:mb-3 sm:text-xl">
                {group.portName}
              </h2>

              {/* Price Grid */}
              <div className="space-y-1 sm:space-y-2">
                {/* Headers - Desktop only */}
                <div className="mb-1 hidden grid-cols-4 gap-4 text-xs font-medium uppercase text-gray-500 dark:text-gray-400 sm:grid">
                  <div>Type Name</div>
                  <div className="text-right">Date</div>
                  <div className="text-right">Price</div>
                  <div className="text-right">Change</div>
                </div>

                {/* Data Rows */}
                {group.prices.map((price, index) => (
                  <div
                    key={`${price.portId}-${price.typeName}-${index}`}
                    className="flex items-center justify-between gap-2 text-sm sm:grid sm:grid-cols-4 sm:gap-4"
                  >
                    <div className="min-w-0 flex-1 sm:flex-none">
                      <div className="truncate font-medium text-gray-900 dark:text-white">
                        {price.typeName}
                      </div>
                      <div className="text-xs text-gray-500 dark:text-gray-400 sm:hidden">
                        {formatDate(price.date)}
                      </div>
                    </div>
                    <div className="hidden text-right text-gray-600 dark:text-gray-300 sm:block">
                      {formatDate(price.date)}
                    </div>
                    <div className="shrink-0 text-right">
                      <span className="font-semibold text-gray-900 dark:text-white">
                        {formatPrice(price.price)}
                      </span>
                      <div
                        className={`text-xs font-medium sm:hidden ${getChangeColorClass(price.change)}`}
                      >
                        {price.change > 0 ? '+' : ''}
                        {price.change.toFixed(2)}
                      </div>
                    </div>
                    <div
                      className={`hidden text-right text-sm font-medium sm:block ${getChangeColorClass(price.change)}`}
                    >
                      {price.change > 0 ? '+' : ''}
                      {price.change.toFixed(2)}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Expand Icon - Bottom center */}
          <div className="mt-2 flex justify-center">
            <svg
              className={`h-5 w-5 text-gray-400 transition-transform dark:text-gray-500 ${
                isExpanded ? 'rotate-180' : ''
              }`}
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M19 9l-7 7-7-7"
              />
            </svg>
          </div>
        </div>
      </button>

      {/* Accordion Content */}
      {isExpanded && (
        <div className="border-t border-gray-200 bg-gray-50/50 px-4 py-4 dark:border-gray-700 dark:bg-gray-800/50 sm:px-6 sm:py-6">
          {/* Period Selector */}
          <div className="mb-4 flex items-center justify-between">
            <h3 className="text-base font-semibold text-gray-900 dark:text-white sm:text-lg">
              Historical Trends
            </h3>
            <div className="grid grid-cols-4 gap-1 rounded-lg bg-gray-200 p-1 dark:bg-gray-700 sm:gap-1.5 sm:p-1.5">
              {periods.map((period) => (
                <Button
                  key={period.value}
                  size="sm"
                  variant={selectedPeriod === period.value ? 'primary' : 'secondary'}
                  onClick={(e) => {
                    e.stopPropagation();
                    onPeriodChange(period.value as HistoricalPeriod);
                  }}
                  className={`whitespace-nowrap text-xs ${
                    selectedPeriod === period.value
                      ? ''
                      : 'bg-transparent hover:bg-gray-300 dark:hover:bg-gray-600'
                  }`}
                >
                  {period.label}
                </Button>
              ))}
            </div>
          </div>

          {/* Chart */}
          <BunkerPriceChart
            key={`${group.portId}-${selectedPeriod}`}
            history={historyData}
            loading={historyLoading}
            selectedPeriod={selectedPeriod}
          />
        </div>
      )}
    </div>
  );
}
